import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Image,
  Text,
  Heading,
  VStack,
  SimpleGrid,
  Grid,
  Input,
  Textarea,
  FormLabel,
  FormControl,
  useDisclosure,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Stat,
  StatLabel,
  StatNumber,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Portal,
  HStack,
  CloseButton,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ArrowBackIcon,
  DeleteIcon,
  ChatIcon,
  ViewIcon,
  AddIcon,
  EditIcon,
} from '@chakra-ui/icons';
import AuthContext from '../context/AuthContext';
import { characterService, sessionService } from '../services/api';
import { useAppToast } from '../utils/toast';

function CharacterView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const { showErrorToast, showSuccessToast } = useAppToast();
  const [character, setCharacter] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionName, setSessionName] = useState('');
  const cancelRef = React.useRef();
  
  // Modal controls
  const { 
    isOpen: isCreateSessionOpen, 
    onOpen: onOpenCreateSession, 
    onClose: onCloseCreateSession 
  } = useDisclosure();
  
  const { 
    isOpen: isDeleteAlertOpen, 
    onOpen: onOpenDeleteAlert, 
    onClose: onCloseDeleteAlert 
  } = useDisclosure();
  
  // Color scheme
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const formBg = useColorModeValue('gray.50', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const accordionExpandedBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchCharacter();
  }, [id, token]);

  const fetchCharacter = async () => {
    try {
      setIsLoading(true);
      const data = await characterService.getCharacterById(id);
      setCharacter(data);
      setProfile(data.character_profile);

      // Fetch sessions for this character if it's public, user is admin, or user owns the character
      const currentUserId = user?.id;
      if (data.isPublic || user?.role === 'admin' || data.createdBy === currentUserId) {
        const sessionsData = await sessionService.getSessionsByCharacter(id);
        setSessions(sessionsData);
      }
    } catch (error) {
      showErrorToast('Error fetching character', error.response?.data?.error || error.message);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const cmToFeetInches = (cm) => {
    const inches = cm / 2.54;
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    return { feet, inches: remainingInches };
  };

  const createSession = async (e) => {
    e.preventDefault();
    if (!sessionName) return;

    try {
      await sessionService.createSession(sessionName, id);

      setSessionName('');
      onCloseCreateSession();

      // Refresh sessions
      const sessionsData = await sessionService.getSessionsByCharacter(id);
      setSessions(sessionsData);

      showSuccessToast('Session created', 'Your new session has been created successfully.');
    } catch (error) {
      showErrorToast('Error creating session', error.response?.data?.error || error.message);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await sessionService.deleteSession(sessionId);

      // Refresh sessions list
      const sessionsData = await sessionService.getSessionsByCharacter(id);
      setSessions(sessionsData);

      showSuccessToast('Session deleted', 'The session has been deleted successfully.');
    } catch (error) {
      showErrorToast('Error deleting session', error.response?.data?.error || error.message);
    }
  };

  const deleteCharacter = async () => {
    try {
      await characterService.deleteCharacter(id);

      navigate('/');

      showSuccessToast('Character deleted', 'The character has been deleted successfully.');
    } catch (error) {
      showErrorToast('Error deleting character', error.response?.data?.error || error.message);
    }
  };

  // Check if current user is the owner of the character
  const isOwner = character && character.createdBy === user?.id;
  const isAdmin = user?.role === 'admin';
  const canDelete = (isOwner || isAdmin) && (!sessions || sessions.length === 0);
  const heightDisplay = profile ? cmToFeetInches(profile.core_identity?.height_cm || 0) : { feet: 0, inches: 0 };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="70vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!profile) {
    return (
      <Box textAlign="center" py={10}>
        <Heading size="lg">Character not found</Heading>
        <Button mt={4} onClick={() => navigate('/')} colorScheme="brand">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const ReadOnlyField = ({ label, value }) => (
    <FormControl mb={4}>
      <FormLabel fontWeight="medium">{label}</FormLabel>
      <Input
        value={value || ''}
        readOnly
        bg={formBg}
        focusBorderColor="brand.500"
      />
    </FormControl>
  );

  const ReadOnlyTextarea = ({ label, value, rows = 3 }) => (
    <FormControl mb={4}>
      <FormLabel fontWeight="medium">{label}</FormLabel>
      <Textarea
        value={value || ''}
        readOnly
        rows={rows}
        bg={formBg}
        focusBorderColor="brand.500"
      />
    </FormControl>
  );

  const ArrayField = ({ label, values = [] }) => {
    // Ensure values is always an array
    const valuesArray = Array.isArray(values) ? values : [];
    
    return (
      <FormControl mb={4}>
        <FormLabel fontWeight="medium">{label}</FormLabel>
        <VStack align="stretch" spacing={2}>
          {valuesArray.map((value, i) => (
            <Input 
              key={i}
              value={value || ''}
              readOnly
              bg={formBg}
              focusBorderColor="brand.500"
            />
          ))}
          {valuesArray.length === 0 && (
            <Text color="gray.500" fontSize="sm">No items</Text>
          )}
        </VStack>
      </FormControl>
    );
  };

  return (
    <Box bg={pageBg} minH="calc(100vh - 180px)" py={8}>
      <Container maxW="container.xl">
        <Flex mb={6} justifyContent="space-between" alignItems="center">
          <Button
            as={RouterLink}
            to="/"
            leftIcon={<ArrowBackIcon />}
            colorScheme="brand"
            variant="outline"
          >
            Back to Dashboard
          </Button>
          
          <Heading size="lg" textAlign="center" flex="1" mx={4}>
            {profile.character_name}
          </Heading>
          
          <Flex gap={3}>
            {isOwner && (
              <Button
                as={RouterLink}
                to={`/edit-character/${id}`}
                leftIcon={<EditIcon />}
                colorScheme="brand"
              >
                Edit
              </Button>
            )}
            
            {(isOwner || isAdmin) && (
              <Tooltip label={sessions.length > 0 ? "Delete all sessions first" : "Delete character"}>
                <Button
                  leftIcon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={onOpenDeleteAlert}
                  isDisabled={sessions.length > 0}
                >
                  Delete
                </Button>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        
        <Flex 
          direction={{ base: 'column', md: 'row' }}
          gap={8}
          mb={8}
        >
          <Box flex={{ base: '1', md: '1' }}>
            <Card mb={6} variant="outline">
              <CardHeader pb={0}>
                <Heading size="md">Character Information</Heading>
              </CardHeader>
              <CardBody>
                <Badge colorScheme={character.isPublic ? "green" : "blue"} mb={4}>
                  {character.isPublic ? "Public Character" : "Private Character"}
                </Badge>
                
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                  <Stat>
                    <StatLabel>Height</StatLabel>
                    <StatNumber>{heightDisplay.feet}'{heightDisplay.inches}"</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Weight</StatLabel>
                    <StatNumber>{Math.round((profile.core_identity?.weight_kg || 0) * 2.20462)} lbs</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Age</StatLabel>
                    <StatNumber>{profile.core_identity?.age || 'N/A'}</StatNumber>
                  </Stat>
                </SimpleGrid>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <ReadOnlyField 
                    label="Gender" 
                    value={profile.core_identity?.gender} 
                  />
                  <ReadOnlyField 
                    label="Race/Species" 
                    value={`${profile.core_identity?.race || ''} ${profile.core_identity?.species || ''}`} 
                  />
                </SimpleGrid>
              </CardBody>
            </Card>
            
            <Card mb={6} variant="outline">
              <CardHeader pb={0}>
                <Heading size="md">Sessions</Heading>
              </CardHeader>
              <CardBody>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="brand"
                  onClick={onOpenCreateSession}
                  mb={4}
                >
                  Create New Session
                </Button>
                
                {sessions.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {sessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map(session => (
                      <Card key={session._id} size="sm" variant="outline">
                        <CardHeader pb={2}>
                          <Heading size="sm">{session.name}</Heading>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {session.messages?.length || 0} messages • Last updated {new Date(session.updatedAt).toLocaleDateString()}
                          </Text>
                        </CardHeader>
                        <CardFooter pt={0} justifyContent="flex-end">
                          <Tooltip label="Open Session">
                            <IconButton
                              icon={<ChatIcon />}
                              colorScheme="brand"
                              size="sm"
                              mr={2}
                              aria-label="Open Session"
                              onClick={() => {
                                const currentUserId = user?.id;
                                // Compare both userId and _id fields since MongoDB might return different formats
                                if (session.userId === currentUserId || session.userId?._id === currentUserId || session.userId?.toString() === currentUserId) {
                                  navigate(`/chat/${session._id}`);
                                } else {
                                  navigate(`/view-session/${session._id}`);
                                }
                              }}
                            />
                          </Tooltip>
                          
                          {(session.userId === user?.id || session.userId?._id === user?.id || session.userId?.toString() === user?.id || isAdmin) && (
                            <Tooltip label="Delete Session">
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                size="sm"
                                aria-label="Delete Session"
                                onClick={() => deleteSession(session._id)}
                              />
                            </Tooltip>
                          )}
                        </CardFooter>
                      </Card>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    No sessions have been created for this character yet.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Box>
          
          {character.imageData && (
            <Box flex={{ base: '1', md: '1' }}>
              <Card variant="outline" overflow="hidden">
                <Image 
                  src={`/api/characters/${id}/image`}
                  alt={profile.character_name}
                  objectFit="contain"
                  width="100%"
                  height="auto"
                />
              </Card>
            </Box>
          )}
        </Flex>
        
        <Accordion allowMultiple defaultIndex={[0]} mt={4}>
              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Core Identity</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <ReadOnlyField 
                      label="Legal Name" 
                      value={profile.core_identity?.names?.legal} 
                    />
                    <ArrayField 
                      label="Nicknames" 
                      values={profile.core_identity?.names?.nicknames} 
                    />
                  </SimpleGrid>
                  <ArrayField 
                    label="Build Descriptors" 
                    values={profile.core_identity?.build?.descriptors} 
                  />
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Physical Appearance</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <ReadOnlyTextarea 
                    label="Face Description" 
                    value={profile.core_identity?.face?.description} 
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <ReadOnlyField 
                      label="Hair Color" 
                      value={profile.core_identity?.face?.hair_color} 
                    />
                    <ReadOnlyField 
                      label="Eye Color" 
                      value={profile.core_identity?.face?.eye_color} 
                    />
                    <ReadOnlyField 
                      label="Skin Tone" 
                      value={profile.core_identity?.face?.skin_tone} 
                    />
                  </SimpleGrid>
                  <ReadOnlyField 
                    label="Body Skin Tone" 
                    value={profile.core_identity?.body?.skin_tone} 
                  />
                  <ReadOnlyTextarea 
                    label="Musculature" 
                    value={profile.core_identity?.body?.musculature} 
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <ArrayField 
                      label="Injuries" 
                      values={profile.core_identity?.body?.injuries} 
                    />
                    <ArrayField 
                      label="Tattoos" 
                      values={profile.core_identity?.body?.tattoos} 
                    />
                    <ArrayField 
                      label="Piercings" 
                      values={profile.core_identity?.body?.piercings} 
                    />
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Backstory & Motivation</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack spacing={4} align="stretch">
                    <FormControl mb={4}>
                      <FormLabel fontWeight="medium">Major Events</FormLabel>
                      <VStack align="stretch" spacing={2}>
                        {profile.backstory_and_motivation?.major_events?.map((value, i) => (
                          <Textarea
                            key={i}
                            value={value || ''}
                            readOnly
                            rows={3}
                            bg={formBg}
                            focusBorderColor="brand.500"
                          />
                        ))}
                        {(!profile.backstory_and_motivation?.major_events || profile.backstory_and_motivation.major_events.length === 0) && (
                          <Text color="gray.500" fontSize="sm">No items</Text>
                        )}
                      </VStack>
                    </FormControl>
                    <ReadOnlyTextarea 
                      label="Primary Motivation" 
                      value={profile.backstory_and_motivation?.primary_motivation} 
                    />
                    <ArrayField 
                      label="Current Motivations" 
                      values={profile.backstory_and_motivation?.current_motivations} 
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Personality & Voice</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <ArrayField 
                      label="Surface Personality" 
                      values={profile.personality_and_voice?.surface_personality} 
                    />
                    <ArrayField 
                      label="Core Personality" 
                      values={profile.personality_and_voice?.core_personality} 
                    />
                  </SimpleGrid>
                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                    <ReadOnlyField 
                      label="Speech Volume" 
                      value={profile.personality_and_voice?.speech_characteristics?.volume} 
                    />
                    <ReadOnlyField 
                      label="Speaking Speed" 
                      value={profile.personality_and_voice?.speech_characteristics?.speed} 
                    />
                  </Grid>
                  <ReadOnlyTextarea 
                    label="Verbal Tics" 
                    value={profile.personality_and_voice?.speech_characteristics?.verbal_tics} 
                    rows={3}
                  />
                  <ReadOnlyTextarea 
                    label="Inflections or Tics" 
                    value={profile.personality_and_voice?.speech_characteristics?.inflections_or_tics} 
                    rows={3}
                  />
                  <ArrayField 
                    label="Sexual Relationship Personality" 
                    values={profile.personality_and_voice?.sexual_relationship_personality} 
                  />
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Sexual Details</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack spacing={4} align="stretch">
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                      <ReadOnlyField 
                        label="Orientation" 
                        value={profile.core_identity?.sexual_details?.orientation} 
                      />
                      <ReadOnlyField 
                        label="Position" 
                        value={profile.core_identity?.sexual_details?.position} 
                      />
                      <ReadOnlyField 
                        label="Cock Length (inches)" 
                        value={profile.core_identity?.sexual_details?.cock_length_inches?.value || 'N/A'} 
                      />
                      <ReadOnlyField 
                        label="Cock Circumference (inches)" 
                        value={profile.core_identity?.sexual_details?.cock_circumference_inches?.value || 'N/A'} 
                      />
                    </Grid>
                    <ReadOnlyTextarea 
                      label="Experience Level" 
                      value={profile.core_identity?.sexual_details?.experience_level} 
                      rows={3}
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Erotic Content Policy</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <ArrayField 
                      label="Allowed Kinks" 
                      values={profile.erotic_content_policy?.allowed_kinks} 
                    />
                    <ArrayField 
                      label="Hard Limits" 
                      values={profile.erotic_content_policy?.hard_limits?.items} 
                    />
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Abilities & Action Style</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack spacing={4} align="stretch">
                    <ArrayField 
                      label="Abilities" 
                      values={profile.abilities_and_action_style?.abilities} 
                    />
                    <ArrayField 
                      label="Traits" 
                      values={profile.abilities_and_action_style?.traits} 
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
                <h2>
                  <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                    <Box flex="1" textAlign="left">
                      <Heading size="md">Style Cheat Sheet</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack spacing={4} align="stretch">
                    <FormControl mb={4}>
                      <FormLabel fontWeight="medium">Examples</FormLabel>
                      <VStack align="stretch" spacing={2}>
                        {profile.style_cheat_sheet?.examples?.map((value, i) => (
                          <Textarea
                            key={i}
                            value={value || ''}
                            readOnly
                            rows={3}
                            bg={formBg}
                            focusBorderColor="brand.500"
                          />
                        ))}
                        {(!profile.style_cheat_sheet?.examples || profile.style_cheat_sheet.examples.length === 0) && (
                          <Text color="gray.500" fontSize="sm">No items</Text>
                        )}
                      </VStack>
                    </FormControl>
                    <ArrayField 
                      label="Guidelines" 
                      values={profile.style_cheat_sheet?.guidelines} 
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
      </Container>

      {/* Create Session Modal */}
      {isCreateSessionOpen && (
        <Portal>
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="blackAlpha.600"
            zIndex="modal"
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={onCloseCreateSession}
          >
            <Box
              bg="white"
              borderRadius="lg"
              boxShadow="xl"
              p={6}
              maxW="md"
              w="full"
              mx={4}
              onClick={(e) => e.stopPropagation()}
            >
              <HStack justify="space-between" mb={4}>
                <Heading size="md">Create New Session</Heading>
                <CloseButton onClick={onCloseCreateSession} />
              </HStack>
              
              <form onSubmit={createSession}>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Session Name</FormLabel>
                    <Input 
                      value={sessionName} 
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="Enter session name"
                    />
                  </FormControl>
                  
                  <HStack justify="flex-end" spacing={3}>
                    <Button variant="outline" onClick={onCloseCreateSession}>
                      Cancel
                    </Button>
                    <Button type="submit" colorScheme="brand" isDisabled={!sessionName.trim()}>
                      Create Session
                    </Button>
                  </HStack>
                </VStack>
              </form>
            </Box>
          </Box>
        </Portal>
      )}

      {/* Delete Character Alert Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseDeleteAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Character
            </AlertDialogHeader>

            <AlertDialogBody>
              {sessions.length > 0 ? (
                "This character has active sessions. Please delete all sessions for this character before attempting to delete the character."
              ) : (
                "Are you sure you want to delete this character? This action cannot be undone."
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseDeleteAlert}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => {
                  deleteCharacter();
                  onCloseDeleteAlert();
                }} 
                ml={3}
                isDisabled={sessions.length > 0}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default CharacterView;